import { Settings, Delete, Add, ContentCopy, PaymentsOutlined } from "@mui/icons-material";
import {
    Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
    DialogTitle, FormControl, FormControlLabel, IconButton, InputLabel, MenuItem, Select,
    Switch, TextField, Tooltip, Typography
} from "@mui/material";
import { SecaoConfiguracao } from "../../../../../core/components/SecaoConfiguracao";
import { IMarketplace } from "../../../../interfaces/IMarketplace";
import { useState } from "react";
import { v4 } from "uuid";
import { IMetodoPagamento, PlataformaPagamentoPix } from "../../../../../core/interfaces/IMetodoPagamento";
import { useMarketplaces } from "../../../../hooks/useMarketplaces";
import { NovoMetodoPagamento } from "../../../../../configuracoes/containers/NovoMetodoPagamento";
import { useConfiguracoesPedidoContext } from "../../../../../pedidos/contexts/ConfiguracoesPedidoContext";
import { useSnackbar } from "../../../../../core/contexts/SnackbarContext";

interface MetodosPagamentoMarketplacePaperProps {
    marketplace: IMarketplace
    refresh: () => void;
}

type CampoCredencialPix = { chave: string; label: string; obrigatorio?: boolean };

const PLATAFORMAS_PIX: { valor: PlataformaPagamentoPix; label: string; campos: CampoCredencialPix[] }[] = [
    {
        valor: "OPENPIX",
        label: "OpenPix",
        campos: [
            { chave: "appId", label: "App ID", obrigatorio: true },
            { chave: "webhookToken", label: "Token do Webhook (opcional)" },
        ],
    },
    {
        valor: "CIELO",
        label: "Cielo",
        campos: [
            { chave: "merchantId", label: "Merchant ID", obrigatorio: true },
            { chave: "merchantKey", label: "Merchant Key", obrigatorio: true },
        ],
    },
];

export const MetodosPagamentoMarketplacePaper = ({ marketplace, refresh }: MetodosPagamentoMarketplacePaperProps) => {
    const [open, setOpen] = useState(false);
    const [chavePix, setChavePix] = useState('');
    const [usarApi, setUsarApi] = useState(false);
    const [plataforma, setPlataforma] = useState<PlataformaPagamentoPix>("OPENPIX");
    const [credenciais, setCredenciais] = useState<Record<string, string>>({});
    const [modalPixAberta, setModalPixAberta] = useState(false);
    const [metodoSelecionado, setMetodoSelecionado] = useState<IMetodoPagamento | null>(null);
    const [loadingCopiar, setLoadingCopiar] = useState(false);

    const { salvar, loading } = useMarketplaces();
    const { configuracao } = useConfiguracoesPedidoContext();
    const snackbar = useSnackbar();

    const handleOpen = () => setOpen(true);
    const handleClose = () => {
        refresh();
        setOpen(false)
    }

    const handleAdicionarMetodoPagamento = async (nomeMetodo: string) => {
        const metodos = marketplace.configuracao?.metodosPagamento;
        const metodosExistentes: IMetodoPagamento[] = Array.isArray(metodos) && typeof metodos[0] !== 'string'
            ? metodos as IMetodoPagamento[]
            : [];

        const novosMetodosPagamento: IMetodoPagamento[] = [
            ...metodosExistentes,
            {
                nome: nomeMetodo,
                uuid: v4(),
                metadados: {}
            }
        ];

        await salvar({
            ...marketplace,
            configuracao: {
                ...marketplace.configuracao,
                metodosPagamento: novosMetodosPagamento
            }
        })
        refresh();
    }

    const handleAbrirModalPix = (metodo: IMetodoPagamento) => {
        setMetodoSelecionado(metodo);
        setChavePix(metodo.metadados?.chave ?? '');
        setUsarApi(metodo.metadados?.usarApi ?? false);
        setPlataforma(metodo.metadados?.plataforma ?? "OPENPIX");
        setCredenciais(metodo.metadados?.credenciais ?? {});
        setModalPixAberta(true);
    }

    const camposPlataformaSelecionada = PLATAFORMAS_PIX.find(p => p.valor === plataforma)?.campos ?? [];

    const credenciaisObrigatoriasPreenchidas = camposPlataformaSelecionada
        .filter(c => c.obrigatorio)
        .every(c => (credenciais[c.chave] ?? '').trim());

    const handleSalvarChavePix = async () => {
        // No modo online o dinheiro cai direto na conta da plataforma do estabelecimento:
        // a chave não é mais usada para repasse, mas as credenciais da plataforma são obrigatórias.
        if (usarApi && !credenciaisObrigatoriasPreenchidas) return;

        const metodos = (marketplace.configuracao?.metodosPagamento ?? []) as IMetodoPagamento[];

        const atualizados = metodos.map(m => {
            if (typeof m === 'string') return m;
            if (m.nome === metodoSelecionado?.nome) {
                return {
                    ...m,
                    metadados: {
                        ...m.metadados,
                        chave: chavePix,
                        usarApi,
                        plataforma,
                        credenciais
                    }
                }
            }
            return m;
        });

        await salvar({
            ...marketplace,
            configuracao: {
                ...marketplace.configuracao,
                metodosPagamento: atualizados
            }
        });

        setModalPixAberta(false);
        setMetodoSelecionado(null);
        refresh();
    }

    const handleExcluirMetodo = async (nome: string) => {
        const metodos = (marketplace.configuracao?.metodosPagamento ?? []) as IMetodoPagamento[];
        const filtrados = metodos.filter(m => typeof m === 'string' ? m !== nome : m.nome !== nome);

        await salvar({
            ...marketplace,
            configuracao: {
                ...marketplace.configuracao,
                metodosPagamento: filtrados
            }
        });

        refresh();
    }

    const copiarMetodosPagamentoDoPDV = async () => {
        setLoadingCopiar(true);
        try {
            const metodosPDV = configuracao?.metodosPagamento ?? [];
            
            if (!metodosPDV || metodosPDV.length === 0) {
                snackbar("Não há métodos de pagamento configurados no PDV", { severity: "warning" });
                setLoadingCopiar(false);
                return;
            }

            const metodosAtuais = marketplace.configuracao?.metodosPagamento ?? [];
            
            // Converter métodos atuais do marketplace para array de nomes (strings)
            const nomesMetodosAtuais = metodosAtuais.map(metodo => 
                typeof metodo === "string" ? metodo.toLowerCase() : metodo.nome.toLowerCase()
            );

            // Filtrar métodos do PDV que já existem no marketplace
            const metodosParaAdicionar = metodosPDV.filter(
                metodoPDV => !nomesMetodosAtuais.includes(metodoPDV.toLowerCase())
            );

            if (metodosParaAdicionar.length === 0) {
                snackbar("Todos os métodos de pagamento do PDV já estão no marketplace", { severity: "info" });
                setLoadingCopiar(false);
                return;
            }

            // Converter métodos do PDV (strings) para formato do marketplace (IMetodoPagamento[])
            const novosMetodos: IMetodoPagamento[] = metodosParaAdicionar.map(nome => ({
                nome: nome,
                uuid: v4(),
                metadados: {}
            }));

            // Combinar métodos existentes (podem ser strings ou objetos) com os novos
            const metodosExistentesObjetos: IMetodoPagamento[] = [];
            for (const metodo of metodosAtuais) {
                if (typeof metodo !== "string") {
                    metodosExistentesObjetos.push(metodo as IMetodoPagamento);
                }
            }

            const metodosAtualizados: IMetodoPagamento[] = [
                ...metodosExistentesObjetos,
                ...novosMetodos
            ];

            await salvar({
                ...marketplace,
                configuracao: {
                    ...marketplace.configuracao,
                    metodosPagamento: metodosAtualizados
                }
            });

            snackbar(`${metodosParaAdicionar.length} método(s) de pagamento copiado(s) do PDV com sucesso`, { severity: "success" });
            refresh();
        } catch (error) {
            snackbar("Erro ao copiar métodos de pagamento do PDV", { severity: "error" });
        } finally {
            setLoadingCopiar(false);
        }
    };

    return (
        <>
            <SecaoConfiguracao
                titulo="Métodos de pagamento"
                descricao="Formas de pagamento disponíveis no checkout do cardápio"
                icone={<PaymentsOutlined />}
                acoes={
                    <>
                        <Button
                            size="small"
                            variant="outlined"
                            endIcon={loadingCopiar ? <CircularProgress size={16} /> : <ContentCopy />}
                            onClick={copiarMetodosPagamentoDoPDV}
                            disabled={loadingCopiar}
                        >
                            {loadingCopiar ? "Copiando..." : "Copiar do PDV"}
                        </Button>
                        <Button size="small" variant="outlined" endIcon={<Add />} onClick={handleOpen}>
                            Customizado
                        </Button>
                    </>
                }
            >
                {!(marketplace.configuracao?.metodosPagamento ?? []).length ? (
                    <Box sx={{
                        minHeight: "120px",
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.5,
                        justifyContent: "center",
                        alignItems: "center",
                        border: theme => `1px dashed ${theme.palette.divider}`,
                        borderRadius: "12px",
                        p: 2
                    }}>
                        <Typography variant="body2" fontWeight={500}>Nenhum método de pagamento</Typography>
                        <Typography variant="caption" color="text.secondary" textAlign="center">
                            Copie os métodos já usados no PDV ou cadastre um método customizado
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                        {(marketplace.configuracao?.metodosPagamento ?? []).map((metodo, index) => {
                            const isObjeto = typeof metodo !== "string";
                            const nome = isObjeto ? metodo.nome : metodo;

                            const pixOnline = isObjeto && (metodo as IMetodoPagamento).metadados?.usarApi;

                            return (
                                <Box
                                    key={index}
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        gap: 1,
                                        p: 1.25,
                                        borderRadius: "12px",
                                        border: theme => `1px solid ${theme.palette.divider}`
                                    }}
                                >
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0, flexWrap: "wrap" }}>
                                        <Typography variant="body2" fontWeight={500}>{nome}</Typography>
                                        {pixOnline && (
                                            <Chip label="QR Code online" size="small" color="success" variant="outlined" />
                                        )}
                                    </Box>
                                    <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
                                        {nome === "Pix" && isObjeto && (
                                            <Tooltip title="Configurar chave Pix">
                                                <IconButton size="small" onClick={() => handleAbrirModalPix(metodo as IMetodoPagamento)}>
                                                    <Settings fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        <Tooltip title="Excluir método">
                                            <IconButton size="small" onClick={() => handleExcluirMetodo(nome)}>
                                                <Delete fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </Box>
                            )
                        })}
                    </Box>
                )}
            </SecaoConfiguracao>

            <NovoMetodoPagamento
                metodosExistentes={marketplace?.configuracao?.metodosPagamento?.map(metodo => typeof metodo === "string" ? metodo : metodo.nome) ?? []}
                formularioAberto={open}
                fecharFormulario={handleClose}
                adicionarMetodoPagamento={handleAdicionarMetodoPagamento}
                transferList={false}
            />

            <Dialog open={modalPixAberta} onClose={() => setModalPixAberta(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Configurar chave Pix</DialogTitle>
                <DialogContent>
                    <FormControlLabel
                        sx={{ mb: 1, mt: 0.5 }}
                        control={
                            <Switch
                                checked={usarApi}
                                onChange={e => setUsarApi(e.target.checked)}
                            />
                        }
                        label="Cobrar via QR Code (PIX online)"
                    />
                    {usarApi && (
                        <>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                                O cliente verá um QR Code no cardápio e o pedido será confirmado automaticamente após o pagamento. Os valores caem diretamente na conta do estabelecimento na plataforma escolhida.
                            </Typography>

                            <FormControl fullWidth margin="dense">
                                <InputLabel id="plataforma-pix-label">Plataforma de integração</InputLabel>
                                <Select
                                    labelId="plataforma-pix-label"
                                    label="Plataforma de integração"
                                    value={plataforma}
                                    onChange={e => {
                                        setPlataforma(e.target.value as PlataformaPagamentoPix);
                                        setCredenciais({});
                                    }}
                                >
                                    {PLATAFORMAS_PIX.map(p => (
                                        <MenuItem key={p.valor} value={p.valor}>{p.label}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            {camposPlataformaSelecionada.map(campo => {
                                const valor = credenciais[campo.chave] ?? '';
                                const invalido = !!campo.obrigatorio && !valor.trim();
                                return (
                                    <TextField
                                        key={campo.chave}
                                        label={campo.label}
                                        value={valor}
                                        onChange={e => setCredenciais(prev => ({ ...prev, [campo.chave]: e.target.value }))}
                                        fullWidth
                                        variant="outlined"
                                        margin="dense"
                                        required={campo.obrigatorio}
                                        error={invalido}
                                        helperText={invalido ? `Informe ${campo.label}` : ""}
                                    />
                                );
                            })}
                        </>
                    )}
                    <TextField
                        label={usarApi ? "Chave PIX (exibida ao cliente, opcional)" : "Chave PIX de destino"}
                        value={chavePix}
                        onChange={e => setChavePix(e.target.value)}
                        fullWidth
                        variant="outlined"
                        margin="dense"
                        placeholder="CPF, CNPJ, e-mail, telefone ou chave aleatória"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setModalPixAberta(false)}>Cancelar</Button>
                    {loading ? (
                        <CircularProgress size={25} />
                    ) : (
                        <Button
                            variant="contained"
                            onClick={handleSalvarChavePix}
                            disabled={usarApi && !credenciaisObrigatoriasPreenchidas}
                        >
                            Salvar
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </>
    );
}
